import React, { useCallback, useImperativeHandle, useState } from 'react'
import useOnclickOutside from 'react-cool-onclickoutside'
import usePlacesAutocomplete, {
  getDetails,
  getGeocode,
  getLatLng
} from 'use-places-autocomplete'

import Input, {
  IInputProps
} from '@/components-latest/primitives/Controls/Input'
import { GeoMarker } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import Suggestions, {
  SuggestionsOption
} from '@/components-latest/shared/Suggestions'
import { Coords } from '@/ducks/common.types'
import useGoogleMapsScript from '@/hooks/useGoogleMapsScript'

import Styles from './PlacesInput.module.scss'

const highlightText = (text: string, highlight: string) => {
  if (!highlight.trim()) {
    return <span>{text}</span>
  }

  const searchWords = highlight.trim().split(/\s+/)
  const regex = new RegExp(
    `(${searchWords
      .map(word => word.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'))
      .join('|')})`,
    'gi'
  )

  const parts = text.split(regex)

  return (
    <span>
      {parts.map((part, index) =>
        regex.test(part) ? <strong key={index}>{part}</strong> : part
      )}
    </span>
  )
}

interface PlaceDetails {
  coords: Coords
  state: string
  city: string
  country: string
  countryCode: string
  streetNumber: string
  street: string
  postalCode: string
}

interface PlaceInputProps extends Omit<IInputProps, 'onChange'> {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  onSelectAddress?: (
    address: string,
    details: PlaceDetails
  ) => void | Promise<void>
}

const COUNTRY_RESTRICTIONS = [
  'dz',
  'tn',
  'ma',
  'sn',
  'fr',
  'tg',
  'bj',
  'ci',
  'us'
]

const PlaceInput = React.forwardRef<HTMLInputElement, PlaceInputProps>(
  ({ value, onChange, onBlur, onSelectAddress, ...rest }, forwardedRef) => {
    const { isLoaded } = useGoogleMapsScript()
    const [inputElement, setInputElement] = useState<HTMLInputElement | null>(
      null
    )

    useImperativeHandle(forwardedRef, () => inputElement as HTMLInputElement, [
      inputElement
    ])

    const {
      ready,
      suggestions: { status, data },
      setValue: setPlacesValue,
      clearSuggestions
    } = usePlacesAutocomplete({
      requestOptions: {
        componentRestrictions: { country: COUNTRY_RESTRICTIONS }
      },
      debounce: 300,
      initOnMount: isLoaded
    })

    const handleSelect = async (description: string, placeId: string) => {
      setPlacesValue(description, false)
      onChange(description)
      clearSuggestions()

      try {
        const results = await getGeocode({ address: description })
        const { lat, lng } = getLatLng(results[0])

        const details = await getDetails({
          placeId: placeId,
          fields: ['address_components']
        })

        const coords = { lat, lng }

        let state = '',
          city = '',
          country = '',
          countryCode = '',
          streetNumber = '',
          street = '',
          postalCode = ''

        if (typeof details !== 'string') {
          details.address_components?.forEach(component => {
            const types = component.types
            if (types.includes('administrative_area_level_1')) {
              state = component.long_name
            } else if (types.includes('locality')) {
              city = component.long_name
            } else if (types.includes('country')) {
              country = component.long_name
              countryCode = component.short_name
            } else if (types.includes('street_number')) {
              streetNumber = component.long_name
            } else if (types.includes('route')) {
              street = component.long_name
            } else if (types.includes('postal_code')) {
              postalCode = component.long_name
            }
          })
        }

        const placeDetails = {
          coords,
          state,
          city,
          country,
          countryCode,
          streetNumber,
          street,
          postalCode
        }

        if (onSelectAddress) {
          onSelectAddress(description, placeDetails)
        }
      } catch (error) {
        console.error('😱 Error: ', error)
      }
    }

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      setPlacesValue(e.target.value)
      onChange(e.target.value)
    }

    const renderSuggestions = () =>
      data?.map(suggestion => {
        const {
          place_id,
          structured_formatting: { main_text, secondary_text }
        } = suggestion

        return (
          <SuggestionsOption
            key={place_id}
            value={suggestion.description}
            onClick={() => handleSelect(suggestion.description, place_id)}>
            <div className={Styles.option}>
              <span>
                <GeoMarker className={Styles.optionGeoMarker} />
              </span>
              <Text variant="nextBody" asChild>
                <span>
                  {highlightText(
                    `${main_text ? main_text + ' ' : ''}${
                      secondary_text ? secondary_text : ''
                    }`,
                    value
                  )}
                </span>
              </Text>
            </div>
          </SuggestionsOption>
        )
      })

    const clickOutsideRef = useOnclickOutside(() => {
      clearSuggestions()
      onBlur()
    })

    const setRefs = useCallback(
      (node: HTMLInputElement | null) => {
        setInputElement(node)
        clickOutsideRef(node)
      },
      [clickOutsideRef]
    )

    if (!isLoaded) {
      return <div>Loading Google Maps...</div>
    }

    return (
      <>
        <Suggestions
          maxHeight="auto"
          trigger={
            <Input
              className={Styles.input}
              ref={setRefs}
              value={value}
              onChange={handleInput}
              onBlur={onBlur}
              disabled={!ready}
              {...rest}
            />
          }>
          {renderSuggestions()}
        </Suggestions>
      </>
    )
  }
)

export default PlaceInput
